import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776176359206-a1d34436ddff?q=85&w=1920&auto=format&fit=crop";

const lines = [
  <>Poczuj</>,
  <>
    prawdziwy <span className="text-brand">bas</span>
  </>,
  <>w swoim aucie.</>,
];

const lineAnim = (i) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: {
    duration: 1,
    ease: [0.22, 1, 0.36, 1],
    delay: 0.25 + i * 0.13,
  },
});

const EQ_BARS = [0.45, 0.85, 0.6, 1, 0.72, 0.4, 0.9, 0.55];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <picture>
          <source
            media="(max-width: 767px)"
            srcSet="https://images.unsplash.com/photo-1776176359206-a1d34436ddff?q=80&w=800&auto=format&fit=crop"
          />
          <img
            src={HERO_IMG}
            alt="Customowa zabudowa bagażnika z subwooferami"
            className="h-[118%] w-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/20 to-transparent" />
        <div
          className="absolute -left-32 top-1/3 hidden h-[480px] w-[480px] rounded-full bg-brand/20 blur-[140px] md:block"
          aria-hidden="true"
        />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto w-full max-w-7xl px-6 md:px-10 pb-16 md:pb-24 pt-36"
      >
        <motion.p
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-4 text-[11px] md:text-xs font-bold uppercase tracking-[0.3em] text-zinc-300"
        >
          <span className="h-px w-12 bg-brand" aria-hidden="true" />
          Profesjonalny warsztat car audio
        </motion.p>

        <h1 className="mt-6 font-display font-bold uppercase leading-[0.95] tracking-tight text-[clamp(3rem,10vw,8rem)]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(i)}>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-zinc-300"
        >
          Dobór sprzętu, wygłuszanie drzwi matami butylowymi, customowe skrzynie
          basowe i profesjonalne strojenie. Montaż car audio i subbasu bez
          chałturzenia.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#kontakt"
            data-testid="hero-cta-quote"
            className="inline-flex items-center rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-ink transition-all duration-300 hover:bg-brand-dark hover:-translate-y-0.5"
          >
            Darmowa wycena
          </a>
          <a
            href="#kontakt"
            data-testid="hero-cta-book"
            className="inline-flex items-center rounded-full border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:border-brand hover:text-brand"
          >
            Zarezerwuj termin
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 flex items-end justify-between gap-6"
        >
          <div className="flex items-end gap-6 md:gap-10">
            <div
              className="flex items-end gap-1.5 h-9"
              aria-hidden="true"
              data-testid="hero-equalizer"
            >
              {EQ_BARS.map((h, i) => (
                <span
                  key={i}
                  className="eq-bar"
                  style={{ height: `${h * 100}%`, animationDelay: `${i * 0.12}s` }}
                />
              ))}
            </div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              Warsztat stacjonarny — Opalenica
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3 text-zinc-500">
            <span className="text-[11px] uppercase tracking-[0.25em]">
              Przewiń
            </span>
            <ArrowDown size={16} className="animate-bounce text-brand" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
